import { BehaviorSubject, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import {
  BrowserNtfSubscription,
  WebPushSubscriptionData
} from 'src/lib/fliko/models/browserNtf/browserNtf.data';
import { Nullish } from 'src/lib/fliko/types/typeish.type';
import { LocalStorageService } from './local-storage.service';
import { SnackBarService } from './snackBar.service';
import { BrowserNotificationPermission } from '../models/browser-notification-permission.model';
import { BrowserNtf } from './browser-ntf/browser-ntf.constants';
import { BrowserNtfNavigationService } from './browser-ntf/browser-ntf-navigation.service';
import { BrowserNtfPushRuntimeService } from './browser-ntf/browser-ntf-push-runtime.service';
import { BrowserNtfSyncPushOptions } from './browser-ntf/browser-ntf.models';
import { DatabaseService } from './database.service';

@Injectable({
  providedIn: 'root'
})
export class BrowserNtfService {
  private readonly permission$ = new BehaviorSubject<NotificationPermission>(BrowserNotificationPermission.DEFAULT);
  private readonly enabled$ = new BehaviorSubject<boolean>(false);

  private initialized = false;
  private currentUserId: string | null = null;

  constructor(
    private snackBarService: SnackBarService,
    private ctxDS: ContextDataService,
    private localStorageSrv: LocalStorageService,
    private dbSrv: DatabaseService,
    private navigationSrv: BrowserNtfNavigationService,
    private pushRuntimeSrv: BrowserNtfPushRuntimeService
  ) {}

  /**
   * Observable stream with live browser notification permission changes.
   * @return {Observable<NotificationPermission>}
   */
  public get permissionChanges$(): Observable<NotificationPermission> {
    return this.permission$.asObservable();
  }

  /**
   * Observable stream with effective Web Push enabled state for current user/browser.
   * @return {Observable<boolean>}
   */
  public get enabledChanges$(): Observable<boolean> {
    return this.enabled$.asObservable();
  }

  /**
   * `true` when browser implements Notifications API.
   * @return {boolean}
   */
  public get isSupported(): boolean {
    return this.supportsNotifications();
  }

  /**
   * Returns user id currently bound to browser push subscription in local storage.
   * @return {string | null}
   */
  private get boundUserId(): string | null {
    return this.localStorageSrv.browserNtfBoundUserId.get();
  }

  /**
   * Persists user id currently bound to browser push subscription in local storage.
   * When `null` is provided, binding is removed from local storage.
   * @param {string | null} userId
   * @return {void}
   */
  private set boundUserId(userId: string | null) {
    if (userId === null) {
      this.localStorageSrv.browserNtfBoundUserId.remove();
      return;
    }
    this.localStorageSrv.browserNtfBoundUserId.set(userId);
  }

  /**
   * Returns collection used to persist Web Push subscription bindings for users.
   * @return {*}
   */
  private get pushSubscriptionCollection() {
    return this.dbSrv.commonFactory.getCollection<WebPushSubscriptionData>(BrowserNtf.PUSH_SUBSCRIPTION_COLLECTION);
  }

  /**
   * Returns object used to persist user-level browser notification preferences.
   * @return {*}
   */
  private get subscriptionObject() {
    return this.dbSrv.commonFactory.getObject<BrowserNtfSubscription>(BrowserNtf.SUBSCRIPTION_OBJECT);
  }

  /**
   * User browser notification preferences.
   * @return {Observable<BrowserNtfSubscription | Nullish>}
   */
  public getSubscription$(): Observable<BrowserNtfSubscription | Nullish> {
    return this.subscriptionObject.get();
  }

  /**
   * Save user browser notification preferences.
   * @param {BrowserNtfSubscription} subscription
   * @return {Promise<void>}
   */
  public setSubscription(subscription: BrowserNtfSubscription): Promise<void> {
    return this.subscriptionObject.update(subscription);
  }

  /**
   * Performs one-time runtime bootstrap:
   * - reads current browser permission,
   * - applies estate context from startup URL helper query parameter,
   * - starts service worker runtime listeners,
   * - subscribes to current user stream and reacts to user switches.
   * @return {void}
   */
  public initialize(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.permission$.next(this.getPermissionStatus());
    this.navigationSrv.consumeEstateContextFromCurrentUrl();
    this.pushRuntimeSrv.initializeRuntime();

    this.ctxDS.currentUser$.subscribe((user) => {
      if (!user) return;

      const previousUserId = this.currentUserId;
      this.currentUserId = user.id;
      const userSwitched = this.hasUserBindingChanged(previousUserId, user.id);

      void this.syncCurrentUserState(user.id, userSwitched);
    });
  }

  /**
   * Reads current browser notification permission.
   * Unsupported browsers are treated as `denied`.
   * @return {NotificationPermission}
   */
  public getPermissionStatus(): NotificationPermission {
    return this.supportsNotifications() ? Notification.permission : BrowserNotificationPermission.DENIED;
  }

  /**
   * Requests browser notification permission and updates backend push subscription state.
   * - When permission becomes `granted`, it synchronizes browser push token.
   * - When permission becomes `denied`, it shows dedicated warning.
   * Returns final permission state observed after request attempt.
   * @return {Promise<NotificationPermission>}
   */
  public async requestPermission(): Promise<NotificationPermission> {
    if (!this.supportsNotifications()) {
      this.snackBarService.showWarn(BrowserNtf.MSG_UNSUPPORTED);
      return BrowserNotificationPermission.DENIED;
    }

    this.initialize();

    let permission = this.getPermissionStatus();
    if (permission === BrowserNotificationPermission.DEFAULT) {
      try {
        permission = await Notification.requestPermission();
      } catch {
        permission = BrowserNotificationPermission.DENIED;
      }
    }

    this.permission$.next(permission);

    if (permission === BrowserNotificationPermission.GRANTED) {
      await this.subscriptionObject.update({ enabled: true });
      this.snackBarService.showSuccess(BrowserNtf.MSG_PERMISSION_GRANTED);
      const boundUserId = this.boundUserId;
      const currentUserId = this.ctxDS.peekCurrentUserId();
      await this.syncPushSubscriptionForCurrentUser({
        forceResubscribe: !!boundUserId && !!currentUserId && boundUserId !== currentUserId
      });
      await this.refreshEnabledStatus();
    } else if (permission === BrowserNotificationPermission.DENIED) {
      this.snackBarService.showWarn(BrowserNtf.MSG_PERMISSION_DENIED);
      this.enabled$.next(false);
    }

    return permission;
  }

  /**
   * Disables browser push notifications for current user/browser.
   * Browser permission cannot be revoked programmatically, so we remove Push API and DB bindings.
   * @return {Promise<void>}
   */
  public async disableNotifications(): Promise<void> {
    const userId = this.ctxDS.peekCurrentUserId();
    if (!userId) return;

    const subscriptionId = await this.pushRuntimeSrv.unsubscribePushSubscription();
    if (subscriptionId) {
      await this.pushSubscriptionCollection.removeElement(subscriptionId);
    }
    await this.subscriptionObject.update({ enabled: false });
    this.boundUserId = null;
    this.enabled$.next(false);
    this.snackBarService.showSuccess(BrowserNtf.MSG_PERMISSION_DISABLED);
  }

  /**
   * Checks Notifications API capability.
   * @return {boolean}
   */
  private supportsNotifications(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window;
  }

  /**
   * Detects whether effective push binding should be refreshed
   * because active authenticated user changed.
   * @param {string | null} previousUserId
   * @param {string}        currentUserId
   * @return {boolean}
   */
  private hasUserBindingChanged(previousUserId: string | null, currentUserId: string): boolean {
    const boundUserId = this.boundUserId;
    return (!!previousUserId && previousUserId !== currentUserId) || (!!boundUserId && boundUserId !== currentUserId);
  }

  /**
   * Synchronizes browser notification state for active user.
   * @param {string}  userId
   * @param {boolean} userSwitched
   * @return {Promise<void>}
   */
  private async syncCurrentUserState(userId: string, userSwitched: boolean): Promise<void> {
    const userEnabled = await this.isCurrentUserEnabled();
    if (!userEnabled) {
      this.enabled$.next(false);
      return;
    }

    if (this.permission$.value === BrowserNotificationPermission.DEFAULT) {
      await this.autoPromptPermission(userId);
      return;
    }

    if (this.permission$.value === BrowserNotificationPermission.GRANTED) {
      await this.syncPushSubscriptionForCurrentUser({ forceResubscribe: userSwitched });
    }

    await this.refreshEnabledStatus();
  }

  /**
   * Checks persisted user-level browser notification switch.
   * @return {Promise<boolean>}
   */
  private async isCurrentUserEnabled(): Promise<boolean> {
    const subscription = await this.subscriptionObject.first();
    return subscription?.enabled ?? true;
  }

  /**
   * Updates effective enabled state for current browser.
   * @return {Promise<void>}
   */
  private async refreshEnabledStatus(): Promise<void> {
    const enabled =
      this.permission$.value === BrowserNotificationPermission.GRANTED &&
      (await this.isCurrentUserEnabled()) &&
      (await this.pushRuntimeSrv.hasPushSubscription());
    this.enabled$.next(enabled);
  }

  /**
   * Synchronizes browser push subscription for currently active user.
   * If synchronization succeeds, current user is marked as bound in local storage.
   * @param {BrowserNtfSyncPushOptions | undefined} options
   * @return {Promise<void>}
   */
  private async syncPushSubscriptionForCurrentUser(options?: BrowserNtfSyncPushOptions): Promise<void> {
    const vapidPublicKey = environment.webPushPublicKey ?? '';
    const userId = this.ctxDS.peekCurrentUserId();
    if (!userId || !vapidPublicKey) return;

    const pushSubscriptionData = await this.pushRuntimeSrv.syncPushSubscription(userId, vapidPublicKey, options);
    if (!pushSubscriptionData) return;

    await this.pushSubscriptionCollection.setElement(pushSubscriptionData.id, pushSubscriptionData);
    this.boundUserId = userId;
  }

  /**
   * Auto-prompts browser permission once per user and browser profile.
   * Prompt marker is persisted to avoid repeated prompts on each app start.
   * @param {string} userId
   * @return {Promise<void>}
   */
  private async autoPromptPermission(userId: string): Promise<void> {
    if (this.getPermissionStatus() !== BrowserNotificationPermission.DEFAULT) return;

    if (this.localStorageSrv.browserNtfPrompted.get(userId)) return;

    this.localStorageSrv.browserNtfPrompted.set(userId, Date.now().toString());
    await this.requestPermission();
  }
}
